import { useEffect, useRef, useState } from 'react';
import type { MouseEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import helpers from '../../../Utils/helpers';
import Spinner from '../../Fundamentals/Spinner/Spinner';
import DrawerPackages from './DrawerPackages';
import { getInvoiceRowActions } from './invoiceRowActionsVisibility';
import type {
  BalanceSubscription,
  TransactionRow,
  VbInvoice,
} from '../../../Containers/Customer/CustomerTransactions.types';
import './DetailsDrawer.css';
import './transactions.css';

interface DetailsDrawerProps {
  row: TransactionRow;
  onClose: () => void;
  showPackages: boolean;
  packages: BalanceSubscription[];
  isLoadingPackages: boolean;
  vbInvoice: VbInvoice | null;
  isInvoiceAdmin: boolean;
  onSendCopy: (row: TransactionRow) => void;
}

const DELIVERY_METHOD_KEYS: Record<number, string> = {
  1: 'customertransactions.deliveryElectronic',
  2: 'customertransactions.deliveryPrint',
  4: 'customertransactions.deliveryEpdf',
};

const DetailsDrawer = ({
  row,
  onClose,
  showPackages,
  packages,
  isLoadingPackages,
  vbInvoice,
  isInvoiceAdmin,
  onSendCopy,
}: DetailsDrawerProps) => {
  const { t } = useTranslation();

  const { sendCopyDisabled, showDelete, showRegisterRepayment, showCredit } = getInvoiceRowActions(
    row,
    isInvoiceAdmin
  );
  const deliveryKey = DELIVERY_METHOD_KEYS[vbInvoice?.transportService ?? -1];
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
    const frame = window.requestAnimationFrame(() => setShow(true));
    return () => window.cancelAnimationFrame(frame);
  }, []);

  const title =
    row.type === 'uninvoicedSub'
      ? t('customertransactions.uninvoicedOrder')
      : (row.invoiceNumber ?? '—');

  const handleCancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    event.preventDefault();
    onClose();
  };

  const handleBackdropClick = (event: MouseEvent<HTMLDialogElement>) => {
    if (event.target === dialogRef.current) {
      onClose();
    }
  };

  const deliveryStatus = row.isUninvoiced ? (
    <span className="transactions-pill transactions-pill--uninvoiced">
      {t('customertransactions.uninvoiced')}
    </span>
  ) : (
    <span className="transactions-pill transactions-pill--sent">
      {t('customertransactions.sent')}
    </span>
  );

  return (
    <dialog
      ref={dialogRef}
      className="drawer-dialog"
      onCancel={handleCancel}
      onClick={handleBackdropClick}
    >
      <div className={`drawer transactions-drawer${show ? ' show' : ''}`}>
        <div className="drawer-header">
          <h3 className="m-0">{title}</h3>
          <button
            type="button"
            className="close"
            aria-label={t('customertransactions.close') ?? ''}
            onClick={onClose}
          />
        </div>
        <div className="drawer-body">
          <h4 className="transactions-section-title">
            {t('customertransactions.basicInformation')}
          </h4>
          <dl className="transactions-kv">
            <dt>{t('customertransactions.invoiceNumber')}</dt>
            <dd>
              {row.isUninvoiced
                ? t('customertransactions.assignedWhenSent')
                : (row.invoiceNumber ?? '—')}
            </dd>
            <dt>{t('customertransactions.deliveryStatus')}</dt>
            <dd>{deliveryStatus}</dd>
            <dt>{t('customertransactions.date')}</dt>
            <dd>{row.date !== undefined ? helpers.formatDate(row.date) : '—'}</dd>
            {vbInvoice?.dueDate !== undefined && (
              <>
                <dt>{t('customertransactions.dueDate')}</dt>
                <dd>{helpers.formatDate(vbInvoice.dueDate)}</dd>
              </>
            )}
          </dl>

          {showPackages && (
            <>
              <h4 className="transactions-section-title">
                {t('customertransactions.packagesAndServices')}
              </h4>
              {isLoadingPackages ? (
                <Spinner />
              ) : packages.length > 0 ? (
                <DrawerPackages subscriptions={packages} />
              ) : (
                <p className="text-dark-grey">{t('customertransactions.noEntriesFound')}</p>
              )}
            </>
          )}

          <h4 className="transactions-section-title">{t('customertransactions.financials')}</h4>
          <dl className="transactions-kv">
            <dt>{t('customertransactions.amount')}</dt>
            <dd className="transactions-num">{helpers.currencyFormatter(row.amount)}</dd>
            <dt>{t('customertransactions.residual')}</dt>
            <dd className={`transactions-num${row.residual !== 0 ? ' text-red' : ''}`}>
              {helpers.currencyFormatter(row.residual)}
            </dd>
            {row.status !== undefined && (
              <>
                <dt>{t('customertransactions.status')}</dt>
                <dd>{t(`invoiceStatuses.${row.status}`)}</dd>
              </>
            )}
            {row.posted !== undefined && (
              <>
                <dt>{t('customertransactions.posted')}</dt>
                <dd>{t(`invoicePaymentTypes.${row.posted}`)}</dd>
              </>
            )}
          </dl>

          {vbInvoice !== null && (
            <>
              <h4 className="transactions-section-title">
                {t('customertransactions.deliveryInfo')}
              </h4>
              <dl className="transactions-kv">
                <dt>{t('customertransactions.deliveryMethod')}</dt>
                <dd>{deliveryKey !== undefined ? t(deliveryKey) : '—'}</dd>
                <dt>{t('customertransactions.pdfRecipient')}</dt>
                <dd>
                  {vbInvoice.pdfRecipientEmailAddress !== undefined &&
                  vbInvoice.pdfRecipientEmailAddress !== '' ? (
                    <a href={`mailto:${vbInvoice.pdfRecipientEmailAddress}`}>
                      {vbInvoice.pdfRecipientEmailAddress}
                    </a>
                  ) : (
                    t('customertransactions.notYetInvoiced')
                  )}
                </dd>
              </dl>
            </>
          )}

          {vbInvoice?.messages !== undefined && vbInvoice.messages.length > 0 && (
            <>
              <h4 className="transactions-section-title">
                {t('customertransactions.activityLog')}
              </h4>
              <table className="table table-stripe mb-0">
                <thead>
                  <tr>
                    <th className="text-left">{t('customertransactions.date')}</th>
                    <th className="text-left">{t('customertransactions.message')}</th>
                    <th className="text-left">{t('customertransactions.user')}</th>
                  </tr>
                </thead>
                <tbody>
                  {vbInvoice.messages.map((entry, index) => (
                    <tr key={index}>
                      <td>{entry.date !== undefined ? helpers.formatDate(entry.date) : '—'}</td>
                      <td>{entry.message}</td>
                      <td>{entry.user}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="drawer-footer d-flex gap-8">
          {showDelete && (
            <button
              type="button"
              className="btn btn-destructive btn-sm d-inline-flex align-items-center gap-8"
            >
              <span className="vismaicon vismaicon-sm vismaicon-delete m-0" />
              {t('customertransactions.delete')}
            </button>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm d-inline-flex align-items-center gap-8"
            disabled={sendCopyDisabled}
            onClick={() => onSendCopy(row)}
          >
            <span className="vismaicon vismaicon-sm vismaicon-email m-0" />
            {t('customertransactions.sendCopy')}
          </button>
          {showRegisterRepayment && (
            <button
              type="button"
              className="btn btn-secondary btn-sm d-inline-flex align-items-center gap-8"
            >
              <span className="vismaicon vismaicon-sm vismaicon-payment m-0" />
              {t('customertransactions.registerRepayment')}
            </button>
          )}
          {showCredit && (
            <button type="button" className="btn btn-primary btn-sm">
              {t('customertransactions.credit')}
            </button>
          )}
        </div>
      </div>
    </dialog>
  );
};

export default DetailsDrawer;
